/**
 * Element rects for per-frame pointer effects, without a forced layout every frame.
 *
 * Reading `getBoundingClientRect()` after a frame's style writes makes the browser lay out
 * the whole page on the spot. In a render loop that is a full layout per frame, which is
 * what made scrolling past the Storefront, the globe and the footer stutter.
 *
 * @module
 */
import { pointer } from './pointer'

/** The pointer counts as moving for this long after its last move, in ms. */
const MOVING_MS = 600

/**
 * Returns a reader that re-measures while the pointer is moving (hover must be exact then),
 * and otherwise only every `everyFrames` calls, enough to follow a scroll. Holding the
 * pointer still over a target keeps using the last exact rect.
 *
 * @example
 * const stageRect = createRectCache()
 * useFrame(() => {
 *   const rect = stageRect(stageRef.current)
 * })
 */
export function createRectCache(
  everyFrames = 12,
): (element: Element | null | undefined) => DOMRect | null {
  let rect: DOMRect | null = null
  let calls = 0
  return (element) => {
    if (!element) return null
    calls++
    // `lastMove` is 0 until the pointer first moves: early in a page's life that must not
    // read as "just moved" (performance.now() itself starts near 0).
    const moving = pointer.lastMove > 0 && performance.now() - pointer.lastMove < MOVING_MS
    if (!rect || moving || calls % everyFrames === 0) rect = element.getBoundingClientRect()
    return rect
  }
}
